import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Filter, Save, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, cachedGet } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ListingCard, ListingCardSkeleton } from "@/components/ListingCard";
import { GuideLink } from "@/components/GuideLink";
import { LISTING_TYPES, PROVINCES, SEXES, SORT_OPTIONS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";

const FIELDS = [
  "q", "category_id", "subcategory_id", "listing_type", "province", "district", "min_price",
  "max_price", "breed", "sex", "min_age", "max_age", "min_weight", "max_weight", "min_quantity",
  "verified_only", "account_type", "transport", "negotiable", "with_photo", "with_video",
  "since_days", "sort",
];

export default function Search() {
  useSeo({
    title: "Hayvan, Ekipman ve Yem İlanları",
    description:
      "Büyükbaş, küçükbaş, kanatlı, arıcılık, yem ve çiftlik ekipmanı ilanlarını il, ırk, yaş ve ağırlığa göre filtreleyerek keşfedin.",
    path: "/ilanlar",
  });
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const filters = useMemo(() => {
    const o = {};
    FIELDS.forEach((f) => {
      const v = params.get(f);
      if (v !== null && v !== "") o[f] = v;
    });
    return o;
  }, [params]);

  const page = Number(params.get("page") || 1);

  useEffect(() => {
    cachedGet("/catalog/categories").then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    if (filters.category_id)
      api.get("/catalog/breeds", { params: { category_id: filters.category_id } })
        .then((r) => setBreeds(r.data))
        .catch(() => setBreeds([]));
    else setBreeds([]);
  }, [filters.category_id]);

  useEffect(() => {
    if (!user) return setFavIds([]);
    api.get("/favorites").then((r) => setFavIds(r.data.map((l) => l.id))).catch(() => {});
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/listings", { params: { ...filters, page, page_size: 12 } });
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === false || value == null) next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());

  const toggleFav = async (l) => {
    if (!user) return navigate("/giris");
    const isFav = favIds.includes(l.id);
    try {
      if (isFav) {
        await api.delete(`/favorites/${l.id}`);
        setFavIds((f) => f.filter((i) => i !== l.id));
      } else {
        await api.post(`/favorites/${l.id}`);
        setFavIds((f) => [...f, l.id]);
        toast.success("Favorilere eklendi");
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveSearch = async () => {
    if (!user) return navigate("/giris");
    const name = window.prompt("Bu aramaya bir isim verin", filters.q || "Aramam");
    if (!name) return;
    try {
      await api.post("/saved-searches", { name, params: filters, notify: true });
      toast.success("Arama kaydedildi");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const subcats = cats.find((c) => c.id === filters.category_id)?.children || [];
  const activeCount = Object.keys(filters).filter((k) => k !== "sort").length;

  const FilterPanel = (
    <div className="space-y-5" data-testid="filter-panel">
      <Field label="Anahtar kelime">
        <input
          data-testid="filter-q"
          defaultValue={filters.q || ""}
          onBlur={(e) => setFilter("q", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && setFilter("q", e.target.value)}
          className={inputCls}
          placeholder="Ara..."
        />
      </Field>
      <Field label="Kategori">
        <select data-testid="filter-category" value={filters.category_id || ""} onChange={(e) => { setFilter("subcategory_id", ""); setFilter("category_id", e.target.value); }} className={inputCls}>
          <option value="">Tümü</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      {!!subcats.length && (
        <Field label="Alt kategori">
          <select data-testid="filter-subcategory" value={filters.subcategory_id || ""} onChange={(e) => setFilter("subcategory_id", e.target.value)} className={inputCls}>
            <option value="">Tümü</option>
            {subcats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="İlan türü">
        <select data-testid="filter-listing-type" value={filters.listing_type || ""} onChange={(e) => setFilter("listing_type", e.target.value)} className={inputCls}>
          <option value="">Tümü</option>
          {LISTING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="İl">
        <select data-testid="filter-province" value={filters.province || ""} onChange={(e) => setFilter("province", e.target.value)} className={inputCls}>
          <option value="">Tümü</option>
          {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </Field>
      <Field label="İlçe">
        <input data-testid="filter-district" defaultValue={filters.district || ""} onBlur={(e) => setFilter("district", e.target.value)} className={inputCls} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Min fiyat">
          <input data-testid="filter-min-price" type="number" defaultValue={filters.min_price || ""} onBlur={(e) => setFilter("min_price", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Maks fiyat">
          <input data-testid="filter-max-price" type="number" defaultValue={filters.max_price || ""} onBlur={(e) => setFilter("max_price", e.target.value)} className={inputCls} />
        </Field>
      </div>
      {!!breeds.length && (
        <Field label="Irk">
          <select data-testid="filter-breed" value={filters.breed || ""} onChange={(e) => setFilter("breed", e.target.value)} className={inputCls}>
            <option value="">Tümü</option>
            {breeds.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Cinsiyet">
        <select data-testid="filter-sex" value={filters.sex || ""} onChange={(e) => setFilter("sex", e.target.value)} className={inputCls}>
          <option value="">Tümü</option>
          {SEXES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Min yaş (ay)">
          <input data-testid="filter-min-age" type="number" defaultValue={filters.min_age || ""} onBlur={(e) => setFilter("min_age", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Maks yaş (ay)">
          <input data-testid="filter-max-age" type="number" defaultValue={filters.max_age || ""} onBlur={(e) => setFilter("max_age", e.target.value)} className={inputCls} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Min ağırlık">
          <input data-testid="filter-min-weight" type="number" defaultValue={filters.min_weight || ""} onBlur={(e) => setFilter("min_weight", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Min adet">
          <input data-testid="filter-min-quantity" type="number" defaultValue={filters.min_quantity || ""} onBlur={(e) => setFilter("min_quantity", e.target.value)} className={inputCls} />
        </Field>
      </div>
      <Field label="Hesap türü">
        <select data-testid="filter-account-type" value={filters.account_type || ""} onChange={(e) => setFilter("account_type", e.target.value)} className={inputCls}>
          <option value="">Tümü</option>
          <option value="individual">Bireysel</option>
          <option value="corporate">Kurumsal</option>
        </select>
      </Field>
      <Field label="Tarih">
        <select data-testid="filter-since" value={filters.since_days || ""} onChange={(e) => setFilter("since_days", e.target.value)} className={inputCls}>
          <option value="">Tüm zamanlar</option>
          <option value="1">Bugün</option>
          <option value="7">Son 7 gün</option>
          <option value="30">Son 30 gün</option>
        </select>
      </Field>
      <div className="space-y-3 border-t border-border pt-4">
        {[
          ["verified_only", "Sadece doğrulanmış satıcı"],
          ["transport", "Nakliye mevcut"],
          ["negotiable", "Pazarlığa açık"],
          ["with_photo", "Fotoğraflı"],
          ["with_video", "Videolu"],
        ].map(([key, label]) => (
          <label key={key} className="flex cursor-pointer items-center gap-3 text-sm font-medium text-primary">
            <input
              type="checkbox"
              data-testid={`filter-${key}`}
              checked={filters[key] === "true"}
              onChange={(e) => setFilter(key, e.target.checked ? "true" : "")}
              className="h-5 w-5 accent-[hsl(148_35%_16%)]"
            />
            {label}
          </label>
        ))}
      </div>
      <Button variant="outline" onClick={clearAll} data-testid="clear-filters-btn" className="h-12 w-full rounded-lg">
        <X className="mr-2 h-4 w-4" /> Filtreleri Temizle
      </Button>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-primary sm:text-3xl">İlanlar</h1>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="results-count">
            {loading ? "Yükleniyor..." : `${data?.total ?? 0} ilan bulundu`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GuideLink slug="ilan-verme" />
          <select
            data-testid="sort-select"
            value={filters.sort || "newest"}
            onChange={(e) => setFilter("sort", e.target.value)}
            className="h-12 rounded-lg border border-input bg-card px-3 text-sm font-semibold outline-none"
          >
            {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <Button variant="outline" onClick={saveSearch} data-testid="save-search-btn" className="h-12 rounded-lg">
            <Save className="mr-2 h-4 w-4" /> Aramayı Kaydet
          </Button>
          <Button
            variant="outline"
            className="h-12 rounded-lg lg:hidden"
            data-testid="toggle-filters-btn"
            onClick={() => setShowFilters((s) => !s)}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4" /> Filtreler{activeCount ? ` (${activeCount})` : ""}
          </Button>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="hidden rounded-lg border border-border bg-card p-5 lg:block lg:self-start">
          <p className="mb-4 inline-flex items-center gap-2 font-display font-bold text-primary">
            <Filter className="h-4 w-4" /> Filtreler
          </p>
          {FilterPanel}
        </aside>

        {showFilters && (
          <div className="rounded-lg border border-border bg-card p-5 lg:hidden">{FilterPanel}</div>
        )}

        <div>
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <ListingCardSkeleton key={i} />)
              : data?.items?.map((l) => (
                  <ListingCard key={l.id} listing={l} onFavorite={toggleFav} isFavorite={favIds.includes(l.id)} />
                ))}
          </div>

          {!loading && data?.items?.length === 0 && (
            <div className="rounded-lg border border-border bg-card p-12 text-center" data-testid="empty-results">
              <p className="font-display text-lg font-bold text-primary">
                Bu kategoride henüz aktif ilan bulunmuyor.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Filtreleri gevşetip tekrar deneyebilir veya ilk ilanı siz verebilirsiniz.
              </p>
              <Link
                to="/ilan-ver"
                data-testid="empty-results-create-btn"
                className="mt-6 inline-flex h-12 items-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground"
              >
                İlk İlanı Sen Ver
              </Link>
            </div>
          )}

          {!loading && data?.pages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2" data-testid="pagination">
              <Button
                variant="outline"
                disabled={page <= 1}
                data-testid="prev-page-btn"
                className="h-12 rounded-lg"
                onClick={() => setParams(withPage(params, page - 1))}
              >
                Önceki
              </Button>
              <span className="px-3 text-sm font-semibold text-primary">
                {page} / {data.pages}
              </span>
              <Button
                variant="outline"
                disabled={page >= data.pages}
                data-testid="next-page-btn"
                className="h-12 rounded-lg"
                onClick={() => setParams(withPage(params, page + 1))}
              >
                Sonraki
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const withPage = (params, p) => {
  const n = new URLSearchParams(params);
  n.set("page", String(p));
  return n;
};

const inputCls =
  "h-12 w-full rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-secondary";

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-sm font-semibold text-primary">{label}</label>
    {children}
  </div>
);
